import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { basicLogger, withLDProvider } from "launchdarkly-react-client-sdk";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useReportWebVitals } from "next/web-vitals";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { InfoIcon } from "@/assets/icons/general/Info";
import { DefaultAppHead } from "@/components/shared/head/DefaultAppHead";
import { Layout } from "@/components/shared/layout/Layout";
import { PanelProvider } from "@/components/shared/layout/PanelContext";
import { ToastContainer } from "@/components/ui/status/Toast/Toast";
import { useToast } from "@/components/ui/status/Toast/useToast";
import BackgroundProfileUploads from "@/components/uploads/BackgroundProfileUploads";
import { AppConfig } from "@/config/app.config";
import { initA11y } from "@/config/inits/a11y";
import { initSentry } from "@/config/inits/sentry";
import { QueryConfig } from "@/config/query.config";
import { AuthClerkProvider } from "@/data/auth/auth-clerk/auth-clerk.provider";
import { UploadsProvider } from "@/data/uploads/uploads.context";
import { JobSalaryProvider } from "@/data/watchlist/JobSalary.context";
import Providers from "@/providers";
import { AppErrorBoundary } from "@/providers/AppErrorBoundary";
import { Fonts } from "@/styles/font";
import "@/styles/globals.css";

import { logger } from "@/util/logger";

initSentry();
initA11y();

function App({ Component, pageProps }: AppProps) {
  const { i18n } = useTranslation();
  const [queryClient] = useState(() => new QueryClient(QueryConfig));
  const router = useRouter();
  const { neutralToast } = useToast();

  useEffect(() => {
    i18n.on("languageChanged", (lng) => {
      if (typeof document !== "undefined") {
        document.documentElement.setAttribute("lang", lng);
      }
    });
    logger.info("Language Changed 2", i18n.language);
  }, [i18n]);

  useReportWebVitals((metric) => {
    logger.info("Web vitals", metric);
  });

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch("/api/version.json", {
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      const serverVersion = data.version;
      logger.info("Server version", serverVersion);
      const clientVersion = AppConfig.version;
      logger.info("Client version", clientVersion);

      if (serverVersion !== clientVersion && clientVersion !== undefined) {
        logger.info("Version mismatch", serverVersion, clientVersion);
        neutralToast({
          text: "New version of LossDog is available",
          position: "bottom-right",
          icon: InfoIcon,
          actions: [
            {
              text: "Reload",
              onPress: () => {
                router.reload();
              },
              className: "text-secondary-200",
            },
          ],
        });
      }
    } catch (error) {
      logger.error("Error checking version", error);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [neutralToast, router]);

  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  return (
    <AppErrorBoundary>
      <Providers
        providers={[
          { provider: QueryClientProvider, props: { client: queryClient } },
          { provider: AuthClerkProvider },
          { provider: JobSalaryProvider },
          { provider: UploadsProvider },
          { provider: PanelProvider },
        ]}
      >
        <Fonts />
        <DefaultAppHead />
        <Layout>
          <Component {...pageProps} />
          <ToastContainer />
          <BackgroundProfileUploads />
        </Layout>
        {!AppConfig.isProduction && <ReactQueryDevtools initialIsOpen={false} />}
      </Providers>
    </AppErrorBoundary>
  );
}

export default withLDProvider({
  clientSideID: AppConfig.launchDarkly.clientSideId,
  options: {
    bootstrap: "localStorage",
    logger: basicLogger({ level: "error" }),
  },
  // @ts-ignore: LaunchDarkly's types don't really support Next.js
})(App);
